import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { GameTaskDto, TaskDto } from '@/types'
import { isGameTask } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'

export const useTasksStore = defineStore('tasks', () => {
    const userStore = useUserStore()
    const loggerStore = useLoggerStore()
    const layoutStore = useLayoutStore()
    const api = useAPI()
    const { tasks, tasksError } = storeToRefs(userStore)
    const isClaimStarted = ref(false)

    const notClaimedTasks = computed(() => tasks.value.filter((task) => task.status !== 'CLAIMED'))
    const firstTask = computed(() => notClaimedTasks.value[0] as TaskDto)
    const completedGameTasks = computed(() =>
        tasks.value.filter((task) => isGameTask(task) && task.status === 'COMPLETED'),
    )
    const allTasksClaimed = computed(() => tasks.value.length > 0 && notClaimedTasks.value.length === 0)
    const showTasks = computed(() => !allTasksClaimed.value || tasksError.value)

    async function onTaskClaimed(task: TaskDto) {
        if (isGameTask(task)) {
            const gameTask = task as GameTaskDto
            const taskIndex = completedGameTasks.value.findIndex((t) => t.task_id === gameTask.task_id)
            layoutStore.setSurveyModalSurveyInfo({
                questionSeedNum: Math.max(taskIndex, 0),
                hru: gameTask.settings.game.hru,
                taskId: gameTask.task_id,
            })
        } else {
            await api.claimTask(task.task_id)
        }
    }

    async function claimTask(taskId: string) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'claim',
            action: 'click',
        })
        isClaimStarted.value = true
        await userStore.claimUserTasks(taskId)
        isClaimStarted.value = false
    }

    function onTaskStart(taskId: string) {
        const task = tasks.value.find(({ task_id }) => task_id === taskId)
        if (!task) {
            return
        }
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'do-task',
            action: 'click',
        })
    }

    async function onReloadClick() {
        await userStore.getUserTasks()
    }
    return {
        onTaskClaimed,
        onTaskStart,
        onReloadClick,
        claimTask,
        allTasksClaimed,
        notClaimedTasks,
        tasksError,
        showTasks,
        tasks,
        firstTask,
        isClaimStarted,
    }
})
